import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import './Team.css';

const Reveal = ({ children, delay = 0, className = '', direction = 'up' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const variants = {
    up: { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0 } },
    left: { hidden: { opacity: 0, x: -32 }, visible: { opacity: 1, x: 0 } },
    right: { hidden: { opacity: 0, x: 32 }, visible: { opacity: 1, x: 0 } },
    scale: { hidden: { opacity: 0, scale: 0.94 }, visible: { opacity: 1, scale: 1 } },
  };
  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={variants[direction]}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

const team = [
  {
    initials: 'VL',
    photo: '/team/vedavati.jpg',
    name: 'Veda Lolge',
    role: 'FOUNDER & CEO',
    venture: 'The Prisma Global · All Ventures',
    bio: 'Serial entrepreneur with a decade of experience building impact-driven ventures across EdTech, cultural technology, sustainable manufacturing, and digital services. Architect of the Prisma ecosystem.',
    tags: ['Ecosystem Strategy', 'Venture Architecture', 'Impact Leadership'],
  },
  {
    initials: 'AS',
    photo: '/team/abu.jpg',
    name: 'Abu Sufiyan Kazi',
    role: 'SITE & INFRASTRUCTURE ENGINEER',
    venture: 'Prisma Terra · Real Estate & Infrastructure',
    bio: 'Civil engineer with 4+ years of site engineering experience across smart city and construction projects in Solapur and Pune. Leads on-ground infrastructure execution and technical oversight for Prisma Terra.',
    tags: ['Site Engineering', 'Infrastructure', 'AutoCAD'],
  },
  {
    initials: 'AK',
    photo: '/team/amit.jpg',
    name: 'Amit Kulkarni',
    role: 'JUNIOR OPERATIONS EXECUTIVE',
    venture: 'Prisma Terra · Real Estate & Projects',
    bio: "Solapur-based operations professional with hands-on experience in civil site follow-up, real estate unit sales, and local regulatory paperwork. Brings ground-level intelligence to Prisma's property operations.",
    tags: ['Real Estate Ops', 'Site Coordination', 'Local Compliance'],
  },
];

const Team = () => {
  const navigate = useNavigate();

  return (
    <div className="prisma-team-page" data-testid="team-page">
      <SEO
        title="The Team | The Prisma Global"
        description="A small, deliberate team building Prisma's multi-venture ecosystem from Solapur, Maharashtra."
        canonical="/team"
      />
      <Navbar />

      {/* SECTION 1 — HERO */}
      <section className="team-hero" data-testid="team-hero-section">
        <div className="team-section-inner">
          <Reveal>
            <p className="team-eyebrow">The People Behind Prisma</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="team-hero-headline">
              Built by people who<br/>believe in the work.
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="team-hero-sub">
              A small, deliberate team &mdash; each person chosen for depth of expertise and alignment with Prisma&rsquo;s founding values.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTION 2 — TEAM CARDS */}
      <section className="team-cards-section section-light" data-testid="team-cards-section">
        <div className="team-section-inner">
          <div className="team-cards-grid">
            {team.map((m, i) => (
              <Reveal key={m.initials} delay={i * 0.12} direction="scale">
                <article className="team-card" data-testid={`team-card-${m.initials.toLowerCase()}`}>
                  <div className="team-avatar" aria-label={`${m.name} photo`}>
                    {m.photo ? (
                      <img
                        src={m.photo}
                        alt={m.name}
                        className="team-avatar-photo"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fb = e.currentTarget.nextElementSibling;
                          if (fb) fb.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <span className="team-avatar-initials" style={m.photo ? { display: 'none' } : {}}>{m.initials}</span>
                  </div>

                  <h2 className="team-card-name">{m.name}</h2>
                  <p className="team-card-role">{m.role}</p>
                  <p className="team-card-venture">{m.venture}</p>

                  <p className="team-card-bio">{m.bio}</p>

                  <span className="team-card-divider" />

                  <div className="team-card-tags">
                    {m.tags.map((t) => (
                      <span key={t} className="team-card-tag">{t}</span>
                    ))}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — JOIN THE TEAM */}
      <section className="team-join-section" data-testid="team-join-section">
        <div className="team-section-inner">
          <Reveal>
            <p className="team-eyebrow">Work With Prisma</p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="team-join-headline">
              We build in small teams.<br/>Every person counts.
            </h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="team-join-body">
              Prisma is always open to exceptional people who want to build something that lasts. If your values align with ours, we want to hear from you.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <button
              className="team-join-cta"
              data-testid="team-join-cta"
              onClick={() => navigate('/contact')}
              type="button"
            >
              Start a Conversation <span aria-hidden="true">&rarr;</span>
            </button>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="team-join-location">
              We are based in Solapur, Maharashtra &mdash; and we work with people regardless of location.
            </p>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Team;
